import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import getActiveTabBar from '@functions/tabBar'
import parsePermittedQueryParams from '@functions/utils/parsePermittedQueryParams'

import useBrands from '@hooks/useBrands'
import useClients from '@hooks/useClients'
import { generateUrls } from '@hooks/useClient'

import ClientListItem from '@components/ClientListItem'
import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { BrandModel } from '@models/types'


import type { PageContextPayload } from '../../index'

const buildPageFilterFields = (brands: BrandModel[]) => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Name or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'brandId',
      label: 'Brand',
      values: brands.map(brand => ({ key: brand.id, label: brand.name })),
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { isArchived: archived } = parsePermittedQueryParams(getQueryParamsFromHash(), ['isArchived'])

  const isArchived = archived === 'true'

  const clientsPayload = useClients({
    filters: {
      ...pageFilters,
      isArchived,
    },
    performHttpRequests: true,
  })

  const { filteredBrands } = useBrands({ performHttpRequests: true })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredClients,
    filteredClientsCount,
    hasFilteredClients,
    loading,
  } = clientsPayload

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showImportClientModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { archivedClientsIndexUrl, clientsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Organisations | AIM Digital</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showImportClientModal()} size="medium">
            Import
          </Button>
        )}
        activeTabBarItemKey={getActiveTabBar({ isArchived })}
        tabBarItems={[
          {
            href: clientsIndexUrl,
            icon: faCheckCircle,
            key: 'active',
            title: 'Active',
          },
          {
            href: archivedClientsIndexUrl,
            icon: faBan,
            key: 'archived',
            title: 'Archived',
          },
        ]}
        title="Organisations"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredClients && <ListBlankState />}

            {!loading && hasFilteredClients && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Organisations">
                <Box flexDirection="column">
                  {filteredClients.map(client => (
                    <ClientListItem
                      client={client}
                      key={client.id}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredClientsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(filteredBrands)}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
